import { Burger, Drawer } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router-dom';
import { LogoMark } from '@/components/Ui/Logo';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { useNavCounts } from '@/hooks/useNavCounts';
import { useSessionCheck } from '@/hooks/useSessionCheck';
import { useRustyState } from '../../state';
import classes from './Account.module.css';
import { AccountNavigation } from './AccountNavigation';

/** The signed-in shell: sidebar on wide screens, a top bar with a drawer below 760px. */
export function AccountLayout() {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [drawerOpened, drawer] = useDisclosure(false);
  const isLoggedIn = useRustyState((s) => s.api?.auth?.getSession().isLoggedIn ?? false);

  useSessionCheck({
    auth: useRustyState.getState().api.auth,
    isLoggedIn,
    logout: useRustyState.getState().logout,
    checkInterval: 10000,
  });
  useNavCounts();

  return (
    <div className={classes.shell}>
      <aside className={classes.sidebar}>
        <AccountNavigation />
      </aside>

      <header className={classes.topbar}>
        <Link to={createLanguageURL('/account')} className={classes.brand}>
          <LogoMark />
          <span>{t('nav.brand')}</span>
        </Link>
        <Burger
          opened={drawerOpened}
          onClick={drawer.toggle}
          size="sm"
          aria-expanded={drawerOpened}
          aria-label={drawerOpened ? t('nav.closeMenu') : t('nav.openMenu')}
        />
      </header>

      <Drawer
        opened={drawerOpened}
        onClose={drawer.close}
        size={280}
        withCloseButton={false}
        classNames={{ body: classes.drawerBody, content: classes.drawerContent }}
        aria-label={t('nav.main')}
      >
        <AccountNavigation onNavigate={drawer.close} />
      </Drawer>

      <main className={classes.main}>
        <Outlet />
      </main>
    </div>
  );
}
